import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Trophy } from 'lucide-react';
import { motion } from 'framer-motion';

const GRID_SIZE = 12;
const CELL_SIZE = 50;

const ZenFrogger = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const playerPosRef = useRef({ x: 5, y: 11 });
  const [playerPos, setPlayerPos] = useState({ x: 5, y: 11 });
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const obstaclesRef = useRef([]);
  const animationRef = useRef(null);
  const gameStateRef = useRef({ gameOver: false, won: false, lives: 3 });
  const pendingChecksRef = useRef([]);

  // Keep refs in sync with state
  useEffect(() => {
    playerPosRef.current = playerPos;
  }, [playerPos]);

  useEffect(() => {
    gameStateRef.current = { gameOver, won, lives };
  }, [gameOver, won, lives]);

  const initGame = useCallback(() => {
    obstaclesRef.current = [];
    
    // Create rows of obstacles (lily pads and logs)
    for (let row = 2; row < 10; row++) {
      const speed = (row % 2 === 0 ? 1 : -1) * (0.015 + Math.random() * 0.015);
      const count = 3 + Math.floor(Math.random() * 2);
      
      for (let i = 0; i < count; i++) {
        obstaclesRef.current.push({
          x: (i * GRID_SIZE) / count + Math.random() * 2,
          y: row,
          width: 1.8 + Math.random() * 0.5,
          speed,
          type: row % 3 === 0 ? 'lilypad' : 'log'
        });
      }
    }
  }, []);

  const checkCollision = useCallback((pos) => {
    if (gameStateRef.current.gameOver || gameStateRef.current.won) return;
    if (pos.x !== playerPosRef.current.x || pos.y !== playerPosRef.current.y) return;

    if (pos.y >= 2 && pos.y <= 9) {
      let onSafe = false;

      obstaclesRef.current.forEach(obs => {
        if (Math.floor(obs.y) === pos.y) {
          if (pos.x >= obs.x - 0.5 && pos.x < obs.x + obs.width + 0.5) {
            onSafe = true;
          }
        }
      });

      if (!onSafe) {
        setLives(prev => {
          const newLives = Math.max(0, prev - 1);
          if (newLives <= 0) {
            setGameOver(true);
          } else {
            // Reset position
            const newPos = { x: 5, y: 11 };
            playerPosRef.current = newPos;
            setPlayerPos(newPos);
          }
          return newLives;
        });
      }
    }
  }, []);

  const checkWin = useCallback((pos) => {
    if (gameStateRef.current.gameOver || gameStateRef.current.won) return;
    if (pos.x !== playerPosRef.current.x || pos.y !== playerPosRef.current.y) return;

    if (pos.y === 0) {
      setScore(prev => prev + 100);
      setWon(true);
    }
  }, []);

  const handleMove = useCallback((dx, dy) => {
    if (gameStateRef.current.gameOver || gameStateRef.current.won) return;
    
    const currentPos = playerPosRef.current;
    const newPos = {
      x: Math.max(0, Math.min(GRID_SIZE - 1, currentPos.x + dx)),
      y: Math.max(0, Math.min(GRID_SIZE - 1, currentPos.y + dy))
    };
    
    if (newPos.x !== currentPos.x || newPos.y !== currentPos.y) {
      playerPosRef.current = newPos;
      setPlayerPos({ ...newPos });
      
      // Small delay to let state update, then check
      const timeoutId = setTimeout(() => {
        pendingChecksRef.current = pendingChecksRef.current.filter(id => id !== timeoutId);
        checkCollision(newPos);
        checkWin(newPos);
      }, 50);
      pendingChecksRef.current.push(timeoutId);
    }
  }, [checkCollision, checkWin]);

  // Clear any pending collision/win checks on unmount
  useEffect(() => {
    return () => {
      pendingChecksRef.current.forEach(id => clearTimeout(id));
      pendingChecksRef.current = [];
    };
  }, []);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameStateRef.current.gameOver || gameStateRef.current.won) return;
      
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          e.preventDefault();
          handleMove(0, -1);
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          e.preventDefault();
          handleMove(0, 1);
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          e.preventDefault();
          handleMove(-1, 0);
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          e.preventDefault();
          handleMove(1, 0);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleMove]);

  // Animation loop
  useEffect(() => {
    const animate = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      
      if (gameStateRef.current.gameOver || gameStateRef.current.won) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const ctx = canvas.getContext('2d');
      canvas.width = GRID_SIZE * CELL_SIZE;
      canvas.height = GRID_SIZE * CELL_SIZE;

      // Background
      ctx.fillStyle = '#E0F7FA';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw safe zones (grass)
      ctx.fillStyle = '#81B29A';
      ctx.fillRect(0, 0, canvas.width, CELL_SIZE * 2);
      ctx.fillStyle = '#6A9B84';
      ctx.fillRect(0, (GRID_SIZE - 2) * CELL_SIZE, canvas.width, CELL_SIZE * 2);

      // Draw water
      ctx.fillStyle = '#4FC3F7';
      ctx.fillRect(0, CELL_SIZE * 2, canvas.width, CELL_SIZE * 8);
      
      // Water ripple effect
      ctx.fillStyle = '#29B6F6';
      for (let i = 0; i < 5; i++) {
        const y = CELL_SIZE * 2 + (Date.now() / 50 + i * 100) % (CELL_SIZE * 8);
        ctx.fillRect(0, y, canvas.width, 2);
      }

      // Draw and update obstacles
      obstaclesRef.current.forEach(obs => {
        obs.x += obs.speed;
        
        if (obs.x > GRID_SIZE + 2) obs.x = -obs.width;
        if (obs.x < -obs.width) obs.x = GRID_SIZE + 2;

        const x = obs.x * CELL_SIZE;
        const y = obs.y * CELL_SIZE;
        const width = obs.width * CELL_SIZE;
        
        ctx.shadowBlur = 8;
        ctx.shadowColor = 'rgba(0,0,0,0.3)';
        
        if (obs.type === 'lilypad') {
          // Draw lily pad
          ctx.fillStyle = '#66BB6A';
          ctx.beginPath();
          ctx.ellipse(x + width / 2, y + CELL_SIZE / 2, width / 2, CELL_SIZE / 2.5, 0, 0, Math.PI * 2);
          ctx.fill();
          
          // Lily pad detail
          ctx.fillStyle = '#81C784';
          ctx.beginPath();
          ctx.ellipse(x + width / 2, y + CELL_SIZE / 2, width / 3, CELL_SIZE / 4, 0, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // Draw log
          ctx.fillStyle = '#8D6E63';
          ctx.beginPath();
          ctx.roundRect(x, y + 8, width, CELL_SIZE - 16, 8);
          ctx.fill();
          
          // Log texture
          ctx.fillStyle = '#6D4C41';
          ctx.beginPath();
          ctx.roundRect(x + 5, y + 15, 3, CELL_SIZE - 30, 2);
          ctx.fill();
          ctx.beginPath();
          ctx.roundRect(x + width - 10, y + 12, 3, CELL_SIZE - 24, 2);
          ctx.fill();
        }
      });

      ctx.shadowBlur = 0;

      // Draw player (frog) using ref for accurate position
      const pos = playerPosRef.current;
      const px = pos.x * CELL_SIZE;
      const py = pos.y * CELL_SIZE;
      
      // Frog body
      ctx.fillStyle = '#7CB342';
      ctx.shadowBlur = 5;
      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.beginPath();
      ctx.ellipse(px + CELL_SIZE / 2, py + CELL_SIZE / 2, CELL_SIZE / 2.5, CELL_SIZE / 3, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Back legs
      ctx.fillStyle = '#689F38';
      ctx.beginPath();
      ctx.ellipse(px + CELL_SIZE / 4, py + CELL_SIZE * 0.7, 8, 12, -0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(px + CELL_SIZE * 0.75, py + CELL_SIZE * 0.7, 8, 12, 0.3, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.shadowBlur = 0;
      
      // Eyes (white part)
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(px + CELL_SIZE / 2 - 8, py + CELL_SIZE / 2 - 8, 7, 0, Math.PI * 2);
      ctx.arc(px + CELL_SIZE / 2 + 8, py + CELL_SIZE / 2 - 8, 7, 0, Math.PI * 2);
      ctx.fill();
      
      // Eyes (pupils)
      ctx.fillStyle = '#1B5E20';
      ctx.beginPath();
      ctx.arc(px + CELL_SIZE / 2 - 8, py + CELL_SIZE / 2 - 8, 3, 0, Math.PI * 2);
      ctx.arc(px + CELL_SIZE / 2 + 8, py + CELL_SIZE / 2 - 8, 3, 0, Math.PI * 2);
      ctx.fill();

      // Draw goal area indicator
      ctx.fillStyle = 'rgba(255, 215, 0, 0.3)';
      ctx.fillRect(0, 0, canvas.width, CELL_SIZE);
      ctx.fillStyle = '#FFD700';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('🏆 GOAL 🏆', canvas.width / 2, CELL_SIZE / 2 + 5);

      animationRef.current = requestAnimationFrame(animate);
    };

    initGame();
    animate();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [initGame]);

  const reset = () => {
    pendingChecksRef.current.forEach(id => clearTimeout(id));
    pendingChecksRef.current = [];
    const newPos = { x: 5, y: 11 };
    playerPosRef.current = newPos;
    setPlayerPos(newPos);
    setScore(0);
    setLives(3);
    setGameOver(false);
    setWon(false);
    initGame();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E0F7FA] to-[#B2EBF2] relative overflow-hidden">
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <header className="flex justify-between items-center mb-8">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-2">Zen Frogger</h1>
            <div className="flex gap-6 justify-center text-lg">
              <p className="text-[#4A5568]">Score: <span className="font-bold text-[#81B29A]">{score}</span></p>
              <p className="text-[#4A5568]">Lives: <span className="font-bold text-red-500">{'❤️'.repeat(lives)}</span></p>
            </div>
          </div>
          
          <button
            data-testid="reset-game"
            onClick={reset}
            className="bg-[#81B29A] text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all shadow-lg flex items-center gap-2"
          >
            <RotateCcw className="w-5 h-5" />
            Reset
          </button>
        </header>

        {(gameOver || won) && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-3xl p-8 shadow-2xl border-4 mb-8 text-center"
            style={{ borderColor: won ? '#4CAF50' : '#FF5252' }}
          >
            {won ? (
              <>
                <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-green-600 mb-2">You Made It!</h2>
                <p className="text-xl text-[#4A5568]">Score: {score}</p>
              </>
            ) : (
              <>
                <div className="text-6xl mb-4">🐸💔</div>
                <h2 className="text-3xl font-bold text-red-600 mb-2">Game Over</h2>
                <p className="text-xl text-[#4A5568]">Final Score: {score}</p>
              </>
            )}
            <button
              data-testid="play-again"
              onClick={reset}
              className="mt-6 bg-[#81B29A] text-white px-8 py-3 rounded-full font-bold hover:-translate-y-1 transition-all shadow-lg"
            >
              Play Again
            </button>
          </motion.div>
        )}

        <div className="flex justify-center">
          <div className="bg-white rounded-3xl p-4 md:p-6 shadow-2xl border border-slate-100">
            <canvas ref={canvasRef} className="rounded-2xl" />
          </div>
        </div>

        <div className="mt-6 bg-white rounded-2xl p-4 text-center shadow-lg">
          <p className="text-[#4A5568] font-medium">
            🐸 Guide the frog across! Stay on lily pads 🍃 and logs 🪵 in the water!
          </p>
        </div>

        {/* On-screen touch controls */}
        <div className="mt-6 flex justify-center">
          <div className="bg-white rounded-3xl p-6 shadow-2xl border border-slate-100">
            <div className="grid grid-cols-3 gap-3">
              <div></div>
              <button
                data-testid="move-up"
                onClick={() => handleMove(0, -1)}
                className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#81B29A] to-[#6A9B84] rounded-2xl flex items-center justify-center text-white text-2xl md:text-3xl font-bold shadow-lg hover:-translate-y-1 active:scale-90 transition-all select-none"
                style={{ touchAction: 'manipulation' }}
              >
                ↑
              </button>
              <div></div>
              <button
                data-testid="move-left"
                onClick={() => handleMove(-1, 0)}
                className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#81B29A] to-[#6A9B84] rounded-2xl flex items-center justify-center text-white text-2xl md:text-3xl font-bold shadow-lg hover:-translate-y-1 active:scale-90 transition-all select-none"
                style={{ touchAction: 'manipulation' }}
              >
                ←
              </button>
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center text-4xl">
                🐸
              </div>
              <button
                data-testid="move-right"
                onClick={() => handleMove(1, 0)}
                className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#81B29A] to-[#6A9B84] rounded-2xl flex items-center justify-center text-white text-2xl md:text-3xl font-bold shadow-lg hover:-translate-y-1 active:scale-90 transition-all select-none"
                style={{ touchAction: 'manipulation' }}
              >
                →
              </button>
              <div></div>
              <button
                data-testid="move-down"
                onClick={() => handleMove(0, 1)}
                className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#81B29A] to-[#6A9B84] rounded-2xl flex items-center justify-center text-white text-2xl md:text-3xl font-bold shadow-lg hover:-translate-y-1 active:scale-90 transition-all select-none"
                style={{ touchAction: 'manipulation' }}
              >
                ↓
              </button>
              <div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZenFrogger;
